// WAAP Cache Control — website cache-control rules.
// PUBLIC Open-API surface (root conversant SDK, HMAC) — per-website cache rules:
//   GET    /waap/domains/:domainId/cache           → publicCacheDTO[] (BARE ARRAY)
//   POST   /waap/domains/:domainId/cache           → create one rule
//   PUT    /waap/domains/:domainId/cache/:ruleId    → replace one rule (full body)
//   DELETE /waap/domains/:domainId/cache/:ruleId    → remove one rule
// :domainId anchors the website; the BE resolves the vendor service from it.
//
// Differences vs the old private (/cache-control) surface:
//  - List is a BARE ARRAY (not `{ rules: [...] }`).
//  - Matching uses two fixed slots match_url_path / match_query_string
//    ({operator, patterns}) instead of the private `matches[]`.
//  - There is NO PATCH toggle: enable/disable is a PUT of the full rule body
//    with `enabled` flipped.
//  - No response_headers / allowed_referrers / allowed_origins / auto_gzip_disable.
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { waapKeys, WAAP_AREAS } from './_shared';

// ---- Public DTOs (mirror conversant publicCacheDTO / publicCacheInput) ----

/** A fixed-slot match pattern (mirrors the provider's cacheMatchDTO). */
export interface WaapCacheMatch {
  operator: string;
  patterns: string[];
}

/** A website cache-control rule on the public Open-API surface. */
export interface WaapCacheRule {
  id: number;
  name: string;
  priority: number;
  enabled: boolean;
  ttl: number; // cache TTL seconds; 0 = inherit origin cache TTL
  ignore_client_no_cache: boolean;
  ignore_origin_no_cache: boolean;
  ignore_query_string: boolean;
  enable_x_cache: boolean;
  never_cache: boolean;
  vary_mimes: string[];
  match_url_path?: WaapCacheMatch | null;
  match_query_string?: WaapCacheMatch | null;
}

/** Create/update body — the rule without the server-assigned id. */
export type WaapCacheInput = Omit<WaapCacheRule, 'id'>;

const cachePath = (domainId: string) => `/waap/domains/${domainId}/cache`;

/** Strip the server-assigned id to produce a write body. */
const ruleToInput = (r: WaapCacheRule): WaapCacheInput => {
  const { id: _id, ...rest } = r;
  return rest;
};

/** List a website's cache-control rules (null on 400/404 = none/unconfigured). */
export function useWaapCacheRules(domainId: string | undefined) {
  return useQuery({
    queryKey: waapKeys.aspect(domainId ?? '', WAAP_AREAS.cacheControl),
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<WaapCacheRule[] | null> => {
      try {
        const res = await serviceApiV3.get<WaapCacheRule[] | null>(cachePath(domainId as string));
        return res.data ?? null;
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 400 || status === 404) return null;
        throw e;
      }
    },
  });
}

/** Create one cache-control rule. Invalidates the list. */
export function useCreateWaapCacheRule(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, WaapCacheInput>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: WaapCacheInput) => {
      const res = await serviceApiV3.post(cachePath(domainId), body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: waapKeys.aspect(domainId, WAAP_AREAS.cacheControl) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Replace one cache-control rule (PUT full body). Invalidates the list. */
export function useUpdateWaapCacheRule(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, { id: number; body: WaapCacheInput }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: number; body: WaapCacheInput }) => {
      const res = await serviceApiV3.put(`${cachePath(domainId)}/${id}`, body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: waapKeys.aspect(domainId, WAAP_AREAS.cacheControl) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Enable/disable one rule. No PATCH on the public surface — PUT the full rule
 *  body with `enabled` flipped. Invalidates the list. */
export function useToggleWaapCacheRule(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, WaapCacheRule>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rule: WaapCacheRule) => {
      const body: WaapCacheInput = { ...ruleToInput(rule), enabled: !rule.enabled };
      const res = await serviceApiV3.put(`${cachePath(domainId)}/${rule.id}`, body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: waapKeys.aspect(domainId, WAAP_AREAS.cacheControl) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Delete one cache-control rule by id. Invalidates the list. */
export function useDeleteWaapCacheRule(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, number>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await serviceApiV3.delete(`${cachePath(domainId)}/${id}`);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: waapKeys.aspect(domainId, WAAP_AREAS.cacheControl) });
      options?.onSuccess?.(...args);
    },
  });
}
